/*  Shared  */
import { TABLENAMES } from 'application/Shared/Constants';
import {
    TenantStatsMapInterface,
    useFetchGlobalAdminTenantStat
} from 'hooks/dataAccess/GlobalAdmin/tenantStat';

/*  React  */
import { TFunction, t } from 'i18next';
import React, {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useMemo,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';

export interface GlobalTenantTableContextType {
    baseParameters: Record<string, unknown>;
    loadingTenantStats: boolean;
    openCreateTenant: boolean;
    setOpenCreateTenant: Dispatch<SetStateAction<boolean>>;
    t: TFunction<'translation', undefined>;
    tableName: string;
    tenantStats: TenantStatsMapInterface | undefined;
}

export const GlobalTenantTableContext =
    createContext<GlobalTenantTableContextType>({
        baseParameters: {},
        loadingTenantStats: false,
        openCreateTenant: false,
        setOpenCreateTenant: () => {},
        t,
        tableName: TABLENAMES.GLOBAL_TENANTS,
        tenantStats: {}
    });

export const GlobalTenantTableContextProvider = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const { t } = useTranslation();

    const [openCreateTenant, setOpenCreateTenant] = useState(false);

    const baseParameters = useMemo(
        () => ({
            global: true,
            includeAdmin: true,
            includePartnerDetails: true,
            includeSettings: true,
            name: '*'
        }),
        []
    );

    const { tenantStats, loading: loadingTenantStats } =
        useFetchGlobalAdminTenantStat({ enabled: true });

    const providerValues = useMemo(
        () => ({
            baseParameters,
            loadingTenantStats,
            openCreateTenant,
            setOpenCreateTenant,
            t,
            tableName: TABLENAMES.GLOBAL_TENANTS,
            tenantStats
        }),
        [baseParameters, loadingTenantStats, openCreateTenant, t, tenantStats]
    );

    return (
        <GlobalTenantTableContext.Provider value={providerValues}>
            {children}
        </GlobalTenantTableContext.Provider>
    );
};

export const useGlobalTenantTableContext = () => {
    const context = useContext(GlobalTenantTableContext);

    if (context === undefined) {
        throw new Error(
            '`useGlobalTenantTableContext` must be used within `GlobalTenantTableContextProvider`'
        );
    }

    return context;
};
